import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
    Calendar, Download, Filter, FileText, IndianRupee, TrendingUp, 
    ArrowUpRight, Search, ChevronLeft, ChevronRight
} from 'lucide-react';
import UseAxiosSecure from '../hooks/UseAxiosSecure';
import UseAuth from '../hooks/UseAuth'; // 👈 NEW: Imported Auth Hook

const SalesSpreadsheet = () => {
    const axiosSecure = UseAxiosSecure();
    const { activeBranchId } = UseAuth(); // 👈 NEW: Get active branch ID
    
    const [dateRange, setDateRange] = useState({ start: '', end: '' });
    const [searchTerm, setSearchTerm] = useState('');

    // Pagination State
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 10;

    // Fetching data
    const { data: report, isLoading } = useQuery({
        // 👈 NEW: Added activeBranchId to trigger re-fetch on branch switch
        queryKey: ['salesReport', dateRange, activeBranchId],
        queryFn: async () => {
            const { data } = await axiosSecure.get('/sales-report', {
                params: { startDate: dateRange.start, endDate: dateRange.end }
            });
            return data;
        },
        enabled: !!activeBranchId // 👈 NEW: Wait for branch to be selected
    });

    const allSales = report?.data || [];
    const summary = report?.summary || { totalRevenue: 0, totalProfit: 0 };

    // Search Logic
    const filteredSales = allSales.filter(sale =>
        sale.invoiceNo.toLowerCase().includes(searchTerm.toLowerCase()) ||
        sale.items.some(item => item.productName.toLowerCase().includes(searchTerm.toLowerCase()))
    );

    // Pagination Logic
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentSales = filteredSales.slice(indexOfFirstItem, indexOfLastItem);
    const totalPages = Math.ceil(filteredSales.length / itemsPerPage);

    // CSV Export Logic
    const handleExportCSV = () => {
        if (allSales.length === 0) return;

        const headers = ["Date,Invoice,Products,Revenue,Profit,Staff\n"];
        const rows = allSales.map(sale => {
            const date = new Date(sale.createdAt).toLocaleDateString();
            const products = sale.items.map(i => i.productName).join(' | ');
            return `${date},${sale.invoiceNo},"${products}",${sale.totalAmount},${sale.totalProfit},${sale.soldBy?.name || 'N/A'}\n`;
        });

        const blob = new Blob([headers + rows.join('')], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `Sales_Report_${new Date().toLocaleDateString()}.csv`;
        a.click();
        window.URL.revokeObjectURL(url);
    };

    if (!activeBranchId) return <div className="text-white text-center mt-20">Loading Spreadsheet...</div>;

    return (
        <div className="p-6 space-y-6 min-h-screen">

            {/* --- TOP SUMMARY CARDS --- */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="bg-[#1a103c]/60 backdrop-blur-md p-5 rounded-3xl border border-white/5 relative overflow-hidden group">
                    <div className="flex items-center gap-4 relative z-10">
                        <div className="p-3 bg-pink-500/20 rounded-2xl text-pink-500"><FileText size={24} /></div>
                        <div>
                            <p className="text-[10px] text-slate-400 uppercase tracking-widest">Total Invoices</p>
                            <p className="text-2xl font-bold text-white">{filteredSales.length || 0}</p>
                        </div>
                    </div>
                    <div className="absolute -right-2 -bottom-2 text-white/5 group-hover:text-white/10 transition-colors"><FileText size={80} /></div>
                </div>

                <div className="bg-[#1a103c]/60 backdrop-blur-md p-5 rounded-3xl border border-white/5 relative overflow-hidden group">
                    <div className="flex items-center gap-4 relative z-10">
                        <div className="p-3 bg-cyan-500/20 rounded-2xl text-cyan-500"><span className="text-2xl font-bold">৳</span>
                        </div>
                        <div>
                            <p className="text-[10px] text-slate-400 uppercase tracking-widest">Filtered Revenue</p>
                            <p className="text-2xl font-bold text-white">Tk.{filteredSales.reduce((acc, s) => acc + s.totalAmount, 0).toLocaleString()}</p>
                        </div>
                    </div>
                    <div className="absolute -right-2 -bottom-2 text-white/5 group-hover:text-white/10 transition-colors"><TrendingUp size={80} /></div>
                </div>

                <div className="bg-[#1a103c]/60 backdrop-blur-md p-5 rounded-3xl border border-white/5 relative overflow-hidden group">
                    <div className="flex items-center gap-4 relative z-10">
                        <div className="p-3 bg-emerald-500/20 rounded-2xl text-emerald-500"><ArrowUpRight size={24} /></div>
                        <div>
                            <p className="text-[10px] text-slate-400 uppercase tracking-widest">Filtered Profit</p>
                            <p className="text-2xl font-bold text-white">Tk.{filteredSales.reduce((acc, s) => acc + s.totalProfit, 0).toLocaleString()}</p>
                        </div>
                    </div>
                    <div className="absolute -right-2 -bottom-2 text-white/5 group-hover:text-white/10 transition-colors"><IndianRupee size={80} /></div>
                </div>

                <div className="bg-[#1a103c]/60 backdrop-blur-md p-5 rounded-3xl border border-white/5 flex items-center gap-4">
                    <div className="p-3 bg-purple-500/20 rounded-2xl text-purple-500"><Calendar size={24} /></div>
                    <div className="w-full">
                        <p className="text-[10px] text-slate-400 uppercase tracking-widest mb-1">Date Filter</p>
                        <div className="flex gap-2">
                            <input
                                type="date"
                                onChange={(e) => setDateRange(p => ({ ...p, start: e.target.value }))}
                                className="bg-black/20 text-[10px] text-white border border-white/10 rounded-md p-1 focus:outline-none focus:border-pink-500 w-1/2"
                            />
                            <input
                                type="date"
                                onChange={(e) => setDateRange(p => ({ ...p, end: e.target.value }))}
                                className="bg-black/20 text-[10px] text-white border border-white/10 rounded-md p-1 focus:outline-none focus:border-pink-500 w-1/2"
                            />
                        </div>
                    </div>
                </div>
            </div>

            {/* --- SPREADSHEET TOOLBAR --- */}
            <div className="flex flex-col md:flex-row justify-between items-center bg-[#1a103c]/40 p-4 rounded-2xl border border-white/5 gap-4">
                <div className="relative w-full md:w-96">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" size={16} />
                    <input
                        type="text"
                        placeholder="Search by invoice or product..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="w-full bg-black/20 border border-white/10 rounded-xl py-2 pl-10 pr-4 text-sm text-white focus:outline-none focus:border-pink-500 transition-all"
                    />
                </div>
                <button
                    onClick={handleExportCSV}
                    className="w-full md:w-auto flex items-center justify-center gap-2 bg-pink-600 hover:bg-pink-500 text-white px-6 py-2.5 rounded-xl text-sm font-bold transition-all shadow-lg shadow-pink-600/20 active:scale-95"
                >
                    <Download size={16} /> Export CSV
                </button>
            </div>

            {/* --- MAIN SPREADSHEET --- */}
            <div className="bg-[#1a103c]/60 backdrop-blur-xl border border-white/5 rounded-[2rem] overflow-hidden shadow-2xl relative">
                {isLoading && (
                    <div className="absolute inset-0 bg-[#0f0a1f]/60 backdrop-blur-sm z-20 flex items-center justify-center">
                        <div className="w-12 h-12 border-4 border-pink-500 border-t-transparent rounded-full animate-spin"></div>
                    </div>
                )}

                <div className="overflow-x-auto">
                    <table className="w-full text-left border-collapse">
                        <thead>
                            <tr className="bg-white/5 border-b border-white/10 text-[11px] uppercase tracking-widest text-slate-400">
                                <th className="p-5 font-bold">Timestamp & Invoice</th>
                                <th className="p-5 font-bold">Product Details</th>
                                <th className="p-5 font-bold text-center">Qty</th>
                                <th className="p-5 font-bold text-right">Revenue</th>
                                <th className="p-5 font-bold text-right text-emerald-400">Net Profit</th>
                                <th className="p-5 font-bold text-center">Staff</th>
                            </tr>
                        </thead>
                        <tbody className="text-xs">
                            {currentSales.map((sale) => (
                                <tr key={sale._id} className="border-b border-white/5 hover:bg-white/5 transition-colors group">
                                    <td className="p-5">
                                        <div className="text-white font-bold">{new Date(sale.createdAt).toLocaleString()}</div>
                                        <div className="text-pink-500/70 font-mono text-[10px]">{sale.invoiceNo}</div>
                                    </td>
                                    <td className="p-5">
                                        <div className="space-y-1">
                                            {sale.items.map((item, i) => (
                                                <div key={i} className="flex items-center justify-between text-slate-300">
                                                    <span>• {item.productName}</span>
                                                </div>
                                            ))}
                                        </div>
                                    </td>
                                    <td className="p-5 text-center">
                                        <span className="bg-white/5 px-2 py-1 rounded-md text-white border border-white/10">
                                            {sale.items.reduce((acc, curr) => acc + curr.quantity, 0)}
                                        </span>
                                    </td>
                                    <td className="p-5 text-right font-bold text-white tracking-wide">
                                        Tk.{sale.totalAmount.toFixed(2)}
                                    </td>
                                    <td className="p-5 text-right font-bold text-emerald-400">
                                        Tk.{sale.totalProfit.toFixed(2)}
                                    </td>
                                    <td className="p-5 text-center">
                                        <span className="text-[10px] text-slate-400 px-2 py-1 bg-white/5 rounded-full border border-white/5">
                                            {sale.soldBy?.name || 'N/A'}
                                        </span>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                {/* --- PAGINATION FOOTER --- */}
                <div className="bg-white/5 p-5 border-t border-white/10 flex justify-between items-center">
                    <p className="text-[10px] text-slate-500 uppercase tracking-widest">
                        Page {currentPage} of {totalPages || 1}
                    </p>
                    <div className="flex gap-2">
                        <button
                            disabled={currentPage === 1}
                            onClick={() => setCurrentPage(p => p - 1)}
                            className="p-2 bg-white/5 rounded-lg text-white disabled:opacity-30 disabled:cursor-not-allowed hover:bg-white/10 transition-colors"
                        >
                            <ChevronLeft size={18} />
                        </button>
                        <button
                            disabled={currentPage === totalPages || totalPages === 0}
                            onClick={() => setCurrentPage(p => p + 1)}
                            className="p-2 bg-white/5 rounded-lg text-white disabled:opacity-30 disabled:cursor-not-allowed hover:bg-white/10 transition-colors"
                        >
                            <ChevronRight size={18} />
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default SalesSpreadsheet;